import { useEffect, useMemo, useRef } from 'react';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  buildKpiTrendsHourFilterValuePatch,
  isKpiTrendsHourlyDateType,
  KPI_TRENDS_HOURLY_FILTER_KEY_LIST,
  KPI_TRENDS_HOURLY_SOURCE_METRICS,
  getHourlyTableMetricOptions,
  normalizeFilterValueForRender,
  patchFilterValueCascaderDefaults,
  clampHourRangeInFilterPayload,
} from '../hourSegmentUtils';
import {
  getMetricOptionByValue,
  DEFAULTKPITRENDSSHOWRTLIST,
} from '../components/MetricsSelection/config';

export interface IUseKpiTrendsHourSegmentOptions {
  filterValue: Record<string, any>;
  setFilterValue: React.Dispatch<React.SetStateAction<any>>;
  defaultFilterValue: Record<string, any>;
  defaultInputList: any[];
  filterKeyList: string[];
  setByDevice: (value: boolean) => void;
  setByCountry: (value: boolean) => void;
}

/**
 * Hour 作为 KPI Trends 第四个 time segment 的专用逻辑层。
 * Day/Week/Month 行为不变；Hour 差异（精简筛选、锁定指标、UI 裁剪）集中在此 Hook。
 * 离开 Hour 的 filter / visual metric 恢复在 fetchFilterChange 统一处理。
 */
export const useKpiTrendsHourSegment = ({
  filterValue,
  setFilterValue,
  defaultFilterValue,
  defaultInputList,
  filterKeyList,
  setByDevice,
  setByCountry,
}: IUseKpiTrendsHourSegmentOptions) => {
  const prevRangeTimeTypeRef = useRef<string>('');
  const rangeTimeType = filterValue?.[filterKeyMap.RANGE_TIME]?.type;
  const metricsOptionsByConfigOrder =
    filterValue?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value
      ?.metricsOptionsByConfigOrder;

  const isHourlyMode = useMemo(
    () => isKpiTrendsHourlyDateType(rangeTimeType),
    [rangeTimeType],
  );

  const activeFilterKeyList = useMemo(
    () => (isHourlyMode ? KPI_TRENDS_HOURLY_FILTER_KEY_LIST : filterKeyList),
    [isHourlyMode, filterKeyList],
  );

  const activeDefaultInputList = useMemo(() => {
    if (!isHourlyMode) return defaultInputList;
    const allowedKeys = new Set(KPI_TRENDS_HOURLY_FILTER_KEY_LIST);
    return defaultInputList.filter((item) => allowedKeys.has(item.key));
  }, [isHourlyMode, defaultInputList]);

  const filterValueForRender = useMemo(() => {
    if (isHourlyMode) {
      // Hour 模式 UI 不展示 Cascader，但 My Filter 回填后可能写入无效 value
      return patchFilterValueCascaderDefaults(filterValue, defaultFilterValue);
    }
    return normalizeFilterValueForRender(filterValue, defaultFilterValue);
  }, [filterValue, isHourlyMode, defaultFilterValue]);

  useEffect(() => {
    const currentType = rangeTimeType;
    if (!currentType || currentType === prevRangeTimeTypeRef.current) return;

    if (isKpiTrendsHourlyDateType(currentType)) {
      setByDevice(false);
      setByCountry(false);
      setFilterValue((prev: Record<string, any>) => ({
        ...prev,
        ...buildKpiTrendsHourFilterValuePatch(
          prev?.[filterKeyMap.RANGE_TIME]?.value,
        ),
      }));
    }

    prevRangeTimeTypeRef.current = currentType;
  }, [rangeTimeType, setFilterValue, setByDevice, setByCountry]);

  const applyHourRangeClampToPayload = (payload: Record<string, any>) => {
    const clamped = clampHourRangeInFilterPayload(payload, filterKeyMap.RANGE_TIME);
    if (clamped !== payload) {
      setFilterValue((prev: Record<string, any>) => ({
        ...prev,
        [filterKeyMap.RANGE_TIME]: clamped[filterKeyMap.RANGE_TIME],
      }));
    }
    return clamped;
  };

  const downloadParamsPatch = useMemo(
    () =>
      isHourlyMode
        ? {
          date_type: 'hourly' as const,
          source_metrics: KPI_TRENDS_HOURLY_SOURCE_METRICS,
        }
        : null,
    [isHourlyMode],
  );

  const selectMetricsOptionsCur = useMemo(() => {
    if (isHourlyMode) return getHourlyTableMetricOptions();
    const trendMetricSelection =
      filterValue?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value || {};
    // 表格列顺序按照 allMetricsOptions 配置顺序（与 Alinea Console 指标顺序一致）
    return (
      trendMetricSelection?.metricsOptionsByConfigOrder ||
      getMetricOptionByValue(trendMetricSelection?.metrics, true) ||
      trendMetricSelection?.metricsOptions ||
      getMetricOptionByValue(DEFAULTKPITRENDSSHOWRTLIST, true) || []
    );
  }, [isHourlyMode, filterValue]);

  return {
    isHourlyMode,
    activeFilterKeyList,
    activeDefaultInputList,
    filterValueForRender,
    pageClassName: isHourlyMode ? 'dbi-KPITrends-compage--hourly' : '',
    applyHourRangeClampToPayload,
    downloadParamsPatch,
    selectMetricsOptionsCur,
    download: {
      businessType: isHourlyMode
        ? ('combine-detail-kpitends-hourly' as const)
        : ('combine-detail-kpitends' as const),
      downloadModalSize: isHourlyMode ? 600 : 820,
    },
    /** Hour segment 下隐藏的能力（与 dateType 驱动的图表层一致） */
    ui: {
      showCalibrationSwitcher: !isHourlyMode,
      showDataSourceExplanation: !isHourlyMode,
      showVisualMetricSelector: !isHourlyMode,
      showSumAvgInTitle: !isHourlyMode,
    },
  };
};

export default useKpiTrendsHourSegment;
